// docs/72 IT15 — the item catalogue as the client draws it: every piece's name, family, rarity,
// sprite (`item_NNN` on the Oryx items sheet) and hue, its worn numbers, powers, active and use, its
// flavour and source, its sounds (each `[bank, variation]`, the variation the Rust table plays) and
// its look (`fx`). Generated by `tools/items/build.mjs` from content/items/catalogue/*.json; the
// rules stay in fk-grammar (these are the same numbers, for words and pictures only).
import gen from '../../content/items.gen.json';

export type Family =
  | 'sword' | 'axe' | 'mace' | 'dagger' | 'spear' | 'bow' | 'crossbow' | 'staff' | 'wand'
  | 'shield' | 'focus' | 'armour' | 'helmet' | 'cape' | 'gloves' | 'boots' | 'legs' | 'belt' | 'neck' | 'ring' | 'charm'
  | 'potion' | 'oil' | 'scroll' | 'bomb' | 'food' | 'tool' | 'tome' | 'treasure' | 'curio';

export type ItemStats = Partial<Record<'base_dmg' | 'reach' | 'range' | 'min_range' | 'guard' | 'hp' | 'move' | 'power' | 'focus' | 'resource' | 'push_resist' | 'pierce', number>>;

export interface StatusSpec { status: string; magnitude: number; pulses?: number; radius?: number }
/** One power, as the catalogue writes it: one key (AUTHORING.md "Powers"). */
export type PowerSpec = Record<string, unknown>;

export type AreaSpec = 'single' | 'self' | 'adjacent' | { blast: number } | { line: number } | { allies_within: number };
export type DmgSpec = 'weapon' | { spell: number } | { flat: number } | { weapon_plus: number };
export type EffectSpec = Record<string, unknown>;
export interface AbilitySpec {
  name: string;
  slot?: 'action' | 'bonus';
  range: number | 'self' | 'reach' | 'weapon';
  los?: boolean;
  area?: AreaSpec;
  effects: EffectSpec[];
  cooldown?: number;
  cost?: number;
}
export type CampSpec = { boon: { card: number; fights: number; party?: boolean } } | { xp: number } | { gold: number } | { card: number } | { hint: number };
export type UseSpec = { fight: AbilitySpec } | { camp: CampSpec };

export type Element = 'arcane' | 'force' | 'fire' | 'frost' | 'lightning' | 'thunder' | 'acid' | 'holy' | 'radiant' | 'shadow' | 'nature' | 'poison' | 'life' | 'smoke' | 'steel' | 'wild';
export type FxForm = 'bolt' | 'darts' | 'ball' | 'beam' | 'nova' | 'column' | 'storm' | 'wall' | 'bloom' | 'aura' | 'curse' | 'blink' | 'cloud' | 'touch' | 'volley';
export type Linger = 'embers' | 'frost' | 'sparks' | 'motes' | 'smoke' | 'puddle' | 'leaves';
export interface FxSpec { element: Element; form?: FxForm; accent?: Element; linger?: Linger }

/** A sound: the bank and the piece's own variation in it. */
export type SfxPick = [bank: string, variation: number];

export interface CatalogueItem {
  id: number;
  name: string;
  family: Family;
  rarity: 1 | 2 | 3 | 4;
  icon: string;
  hue?: number;
  /** `GearSlot` name of a worn piece (rings and charms: `Trinket`, either ring slot). */
  slot?: string;
  classes?: string[];
  two_handed?: boolean;
  size?: number;
  weight?: string;
  stats?: ItemStats;
  powers?: PowerSpec[];
  active?: AbilitySpec;
  use?: UseSpec;
  price?: number;
  loot?: string[];
  /** docs/131 CL6: where it is found (`collection::sources::found` keys: adventure kinds, then stashes). */
  found?: string[];
  /** docs/131 CL6: the keepers that may sell it (`smithy`, `shop`, `inn`, `temple`). */
  sold?: string[];
  /** docs/159 SB2: the counters that always stock it (`market`, `shop`, `smithy`, `inn`, `temple`). */
  staple?: string[];
  flavour: string;
  source: string;
  tags: string[];
  sfx?: { use?: SfxPick; equip?: SfxPick; strike?: SfxPick };
  fx?: FxSpec;
  relic?: boolean;
  /** docs/110: the set this piece is part of. */
  set?: number;
  /** docs/112 UP2: the upgrades a worn piece takes (absent: none). */
  upgrades?: UpgradeKind[];
}

/** docs/112: an enchantment (magic) or an improvement (a craftsman's work). */
export type UpgradeKind = 'enchant' | 'improve';
/** docs/112 UP3: one upgrade as tools/items/upgrades.mjs checks it; `active` is its ability at each rank. */
export interface UpgradeSpec {
  id: number;
  kind: UpgradeKind;
  name: string;
  rarity: 1 | 2 | 3;
  fits: string[];
  max_rank: number;
  per_rank: { stats?: ItemStats; powers?: PowerSpec[] };
  active?: AbilitySpec[];
  fx?: FxSpec;
  shops: string[];
  gift?: boolean;
  look: { glow?: string; particles?: string; badge?: string };
  flavour: string;
  tags: string[];
  source: string;
  /** One rank's base price on a common piece. */
  price: number;
  worth: number;
}

/** docs/110: a set's full-set bonus, as tools/items/sets.mjs checks it. */
export interface SetBonusSpec { stats?: ItemStats; powers?: PowerSpec[]; active: AbilitySpec; fx: FxSpec }
/** What the screen does the moment the last piece goes on (docs/110 SE6). */
export interface FanfareSpec { title: string; colour: string; accent: string; particles?: string; sfx: string }
export interface ItemSetSpec {
  id: number;
  name: string;
  /** Every piece of the set, by def id. */
  members: number[];
  bonus: SetBonusSpec;
  fanfare: FanfareSpec;
  flavour: string;
  tags: string[];
}

/** Where catalogue ids start: everything below is hand-authored (fk-grammar `ITEMS`, docs/10 §6). */
export const CATALOGUE_BASE = 1000;
/** Where item abilities start: `10000 + def` (fk_grammar `ITEM_ABILITY_BASE`). */
export const ITEM_ABILITY_BASE = 10000;

const raw = (gen as unknown as { items: Record<string, Omit<CatalogueItem, 'id'>> }).items;
const rawSets = (gen as unknown as { sets?: Record<string, Omit<ItemSetSpec, 'id'>> }).sets ?? {};
const SETS = new Map<number, ItemSetSpec>(Object.entries(rawSets).map(([k, v]) => [Number(k), { id: Number(k), ...v }]));
/** Where a set's ability id starts (fk_grammar `SET_ABILITY_BASE`): `15000 + set id`. */
export const SET_ABILITY_BASE = 15000;
const rawUps = (gen as unknown as { upgrades?: Record<string, Omit<UpgradeSpec, 'id'>> }).upgrades ?? {};
const UPGRADES = new Map<number, UpgradeSpec>(Object.entries(rawUps).map(([k, v]) => [Number(k), { id: Number(k), ...v }]));
/** Where an upgrade's ability id starts (fk_grammar `UPGRADE_ABILITY_BASE`): `16000 + 5 × id + rank − 1`. */
export const UPGRADE_ABILITY_BASE = 16000;
/** docs/112: an upgrade by id. */
export const upgradeSpec = (id: number): UpgradeSpec | undefined => UPGRADES.get(id);
/** Every upgrade, by id. */
export const upgradeSpecs = (): readonly UpgradeSpec[] => [...UPGRADES.values()].sort((a, b) => a.id - b.id);
export const upgradeAbilityId = (id: number, rank: number): number => UPGRADE_ABILITY_BASE + 5 * id + Math.min(3, Math.max(1, rank)) - 1;
/** The upgrade (and rank) an ability id belongs to. */
export function abilityUpgrade(abilityId: number): { up: UpgradeSpec; rank: number } | undefined {
  if (abilityId < UPGRADE_ABILITY_BASE) return undefined;
  const off = abilityId - UPGRADE_ABILITY_BASE;
  const up = UPGRADES.get(Math.floor(off / 5));
  return up?.active ? { up, rank: (off % 5) + 1 } : undefined;
}
const BY_ID = new Map<number, CatalogueItem>();
for (const [k, v] of Object.entries(raw)) BY_ID.set(Number(k), { id: Number(k), ...v });
const ALL: readonly CatalogueItem[] = [...BY_ID.values()].sort((a, b) => a.id - b.id);

/** The catalogue piece for a def id, if it is one (ids ≥ 1000). */
export const catalogueItem = (def: number): CatalogueItem | undefined => BY_ID.get(def);
/** Every catalogue piece, by id. */
export const catalogueItems = (): readonly CatalogueItem[] => ALL;
export const isCatalogue = (def: number): boolean => BY_ID.has(def);

/** The piece an ability belongs to (`10000 + def`), if it is an item's (a set's is no one piece's). */
export const abilityItem = (abilityId: number): CatalogueItem | undefined => (abilityId >= ITEM_ABILITY_BASE && abilityId < SET_ABILITY_BASE ? BY_ID.get(abilityId - ITEM_ABILITY_BASE) : undefined);

/** docs/131 CL10: for every `found` key, the first level its source gives each rarity, common first (null: never). */
export type FindLevels = Readonly<Record<string, ReadonlyArray<number | null>>>;
const FIND_LEVELS: FindLevels = (gen as unknown as { find_levels?: FindLevels }).find_levels ?? {};
export const findLevels = (): FindLevels => FIND_LEVELS;

/** docs/110: a set by id. */
export const itemSet = (id: number | undefined): ItemSetSpec | undefined => (id === undefined ? undefined : SETS.get(id));
/** Every set, by id. */
export const itemSets = (): readonly ItemSetSpec[] => [...SETS.values()].sort((a, b) => a.id - b.id);
/** The set a piece is part of, if any. */
export const setOf = (def: number): ItemSetSpec | undefined => itemSet(BY_ID.get(def)?.set);
/** The set an ability is the full-set ability of (`15000 + set id`). */
export const abilitySet = (abilityId: number): ItemSetSpec | undefined => (abilityId > SET_ABILITY_BASE && abilityId < UPGRADE_ABILITY_BASE ? SETS.get(abilityId - SET_ABILITY_BASE) : undefined);
/** The sets worn whole among these worn defs (each once, by id): fk_grammar `complete_sets`. */
export function completeSets(worn: Iterable<number>): ItemSetSpec[] {
  const have = new Set(worn);
  const out = new Map<number, ItemSetSpec>();
  for (const d of have) {
    const s = setOf(d);
    if (s && !out.has(s.id) && s.members.every((m) => have.has(m))) out.set(s.id, s);
  }
  return [...out.values()].sort((a, b) => a.id - b.id);
}
export const itemAbilityId = (def: number): number => ITEM_ABILITY_BASE + def;

/** The ability a piece casts when used up in a fight, if it has one. */
export const fightUse = (it: CatalogueItem | undefined): AbilitySpec | undefined => (it?.use && 'fight' in it.use ? it.use.fight : undefined);
/** What a piece does when used out of a fight, if anything. */
export const campUse = (it: CatalogueItem | undefined): CampSpec | undefined => (it?.use && 'camp' in it.use ? it.use.camp : undefined);
/**
 * Whether the Use button for a piece's camp use is offered now (docs/98 AU4, the module's
 * `use_item_in_camp` rules): a hint piece only while its hero is deployed in a puzzle room still
 * open; every other camp use only while its hero is not in a fight.
 */
export function campUseOffered(it: CatalogueItem | undefined, at: { deployed: boolean; inPuzzle: boolean }): boolean {
  const c = campUse(it);
  if (!c) return false;
  return 'hint' in c ? at.deployed && at.inPuzzle : !at.deployed;
}
/** The ability an item ability id stands for: the piece's active or its fight use. */
export function itemAbility(abilityId: number): AbilitySpec | undefined {
  // docs/112: an upgrade's ability at its rank
  const u = abilityUpgrade(abilityId);
  if (u) return u.up.active?.[u.rank - 1];
  const set = abilitySet(abilityId);
  if (set) return set.bonus.active;
  const it = abilityItem(abilityId);
  return it ? it.active ?? fightUse(it) : undefined;
}

/** The pack families (used up or carried, never worn). */
export const PACK_FAMILIES: readonly Family[] = ['potion', 'oil', 'scroll', 'bomb', 'food', 'tool', 'tome', 'treasure', 'curio'];
export const WORN_FAMILIES: readonly Family[] = ['sword', 'axe', 'mace', 'dagger', 'spear', 'bow', 'crossbow', 'staff', 'wand', 'shield', 'focus', 'armour', 'helmet', 'cape', 'gloves', 'boots', 'legs', 'belt', 'neck', 'ring', 'charm'];
export const FAMILIES: readonly Family[] = [...WORN_FAMILIES, ...PACK_FAMILIES];

/** A piece's price when its entry names none, by rarity (tools/items/schema.mjs `DEFAULT_PRICE`). */
export const DEFAULT_PRICE: readonly number[] = [0, 40, 120, 360, 900];
